import { useEffect, useState, type ReactNode } from "react";
import { CloudOff, RotateCw, WifiOff } from "lucide-react";
import { refreshAuth, useAuth } from "../lib/auth";

/** Top-level auth gate. Wraps the entire app shell so that when no
 *  valid portable session is present nothing rendered below this gate
 *  reaches the DOM. Prior shape gated only the Sidebar, which let the
 *  Mail content area keep rendering (and keep fetching from the
 *  bridge proxy) after the user clicked logout. Hoisting the gate is
 *  the only correct fix: a single source of truth instead of every
 *  surface re-checking auth and some forgetting to.
 *
 *  States:
 *    - loading      → render a minimal spinner so the SPA doesn't flash
 *                      content while /auth/me is in flight on first load.
 *    - !authenticated → render the sign-in screen with a Google button
 *                      that links to /auth/google. The Pages function
 *                      proxies that to the daemon which starts the
 *                      OAuth flow.
 *    - authenticated → render the wrapped app shell unchanged.
 *
 *  The hook also listens for tab focus and re-fetches /auth/me so a
 *  logout in one tab eventually flushes another tab on its next focus,
 *  rather than letting a stale "authenticated: true" cache linger
 *  until the next page reload. */
export function AuthGate({ children }: { children: ReactNode }): JSX.Element {
  const auth = useAuth();

  useEffect(() => {
    function onFocus() {
      // Cheap revalidate; if the cookie was cleared elsewhere, the
      // server now answers authenticated:false and the gate flips.
      void refreshAuth();
    }
    // A page Back brings back from the browser's cache (the sign-in page,
    // after signing in) shows what it showed then: ask again.
    function onPageShow(e: PageTransitionEvent) {
      if (e.persisted) void refreshAuth();
    }
    // The connection came back (lib/connectivity.ts): ask again.
    function onReconnected() {
      void refreshAuth();
    }
    window.addEventListener("focus", onFocus);
    window.addEventListener("pageshow", onPageShow);
    window.addEventListener("wov:reconnected", onReconnected);
    return () => {
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pageshow", onPageShow);
      window.removeEventListener("wov:reconnected", onReconnected);
    };
  }, []);

  if (auth.loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-500 dark:text-slate-400"
        style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
        aria-busy
      >
        <div className="text-sm">Loading…</div>
      </div>
    );
  }

  if (!auth.authenticated && auth.unreachable) {
    return <OfflineScreen />;
  }

  if (!auth.authenticated) {
    return <SignInScreen />;
  }

  return <>{children}</>;
}

function SignInScreen(): JSX.Element {
  const returnTo = typeof window !== "undefined" ? window.location.pathname + window.location.search : "/";
  const signInHref = `/auth/google?next=${encodeURIComponent(returnTo)}`;
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-4"
      style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-8 text-center">
        <div className="mx-auto w-12 h-12 rounded-xl overflow-hidden ring-1 ring-slate-200 dark:ring-slate-700 mb-4">
          <img src="/icons/icon-192.png" alt="" aria-hidden className="w-full h-full object-cover" />
        </div>
        <h1 className="text-lg font-semibold tracking-tight text-sky-600 dark:text-sky-400">Waki Mail</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          Sign in with your approved Google account to continue.
        </p>
        <a
          href={signInHref}
          // Replace this page rather than add to it: once signed in, Back
          // should not lead to the sign-in page (2026-10-06).
          onClick={(e) => {
            if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
            e.preventDefault();
            window.location.replace(signInHref);
          }}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white text-sm font-semibold px-4 py-2.5 transition-colors"
        >
          Sign in with Google
        </a>
      </div>
    </div>
  );
}

/** Started without a connection (2026-10-06): the session could not be
 *  checked, so this is not the sign-in page. It keeps checking on its own
 *  (lib/connectivity.ts) and opens the app as soon as the server answers. */
function OfflineScreen(): JSX.Element {
  const [checking, setChecking] = useState(false);
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 text-slate-900 dark:text-slate-100 px-4"
      style={{ fontFamily: "system-ui, -apple-system, sans-serif" }}
    >
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-8 text-center">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-amber-50 text-amber-600 ring-1 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300 dark:ring-amber-900">
          {offline ? <WifiOff className="h-6 w-6" /> : <CloudOff className="h-6 w-6" />}
        </div>
        <h1 className="text-lg font-semibold tracking-tight">{offline ? "You're offline" : "Can't reach Waki Mail"}</h1>
        <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
          {offline
            ? "Connect to the internet and Waki Mail opens by itself."
            : "The server is not answering. Waki Mail keeps trying and opens as soon as it does."}
        </p>
        <button
          type="button"
          disabled={checking}
          onClick={async () => {
            setChecking(true);
            await refreshAuth();
            setChecking(false);
          }}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 active:bg-sky-800 text-white text-sm font-semibold px-4 py-2.5 transition-colors disabled:opacity-60"
        >
          <RotateCw className={"h-4 w-4 " + (checking ? "animate-spin" : "")} /> Try again
        </button>
      </div>
    </div>
  );
}
